import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { SetShoppingStartTime } from '@store/customization/customization.actions';
import { PackageProductActions, TariffProductActions, VoucherProductActions } from '@store/products/product.actions';
import { difference, isEmpty } from 'lodash';
import { filter, map } from 'rxjs';
import { BookingActions } from './booking.actions';
import { selectBookedTariffUuids } from './booking.selectors';
import { PackageProductBookingActions } from './product/package/package-booking.actions';
import { ProductBookingActions } from './product/product-booking.actions';
import { VoucherProductBookingActions } from './product/voucher/voucher-booking.actions';
import { selectAllTicketHolderTariffUuidsFromBookedProducts } from './tariff/booking-tariff.selectors';

export * from '@store/products/booking/countdown/booking-countdown.effects';
export * from '@store/products/booking/product/amano-parking/amano-parking-booking.effects';
export * from '@store/products/booking/product/contingent/contingent-booking.effects';
export * from '@store/products/booking/product/package/package-booking.effects';
export * from '@store/products/booking/product/product-booking.effects';
export * from '@store/products/booking/product/tariff/tariff-booking.effects';
export * from '@store/products/booking/product/voucher/voucher-booking.effects';
export * from '@store/products/booking/product/workshop/workshop-booking.effects';
export * from '@store/products/booking/smart-promotion/booking-smart-promotion.effects';

export const setShoppingStartTime$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        TariffProductActions.setTariffBooking,
        TariffProductActions.setPreferredTariffBooking,
        VoucherProductActions.setVoucherTariffBooking,
        PackageProductActions.setPackageTariffBooking,
        PackageProductActions.setPackageBooking
      ),
      map(() => new SetShoppingStartTime())
    ),
  { functional: true }
);

export const addOrRemoveAddedTariffsForms$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.setProductInBookingList,
        ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
        VoucherProductBookingActions.setVoucherProductInBookingList,
        VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList,
        PackageProductBookingActions.setPackageProductInBookingList
      ),
      concatLatestFrom(() => [
        store.select(selectAllTicketHolderTariffUuidsFromBookedProducts),
        store.select(selectBookedTariffUuids)
      ]),
      filter(([_, currentBookedTariffUuids]) => !isEmpty(currentBookedTariffUuids)),
      map(([{ type }, currentBookedTariffUuids, previousBookedTariffUuids]) => {
        const isAnonymousProductRedeemedAndSetInProductBookingList =
          type === VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList.type;
        const isProductBookingAdded = currentBookedTariffUuids.length >= previousBookedTariffUuids.length;
        const addBookedTariffUuids = difference(currentBookedTariffUuids, previousBookedTariffUuids);
        const removeBookedTariffUuids = difference(previousBookedTariffUuids, currentBookedTariffUuids);

        return isAnonymousProductRedeemedAndSetInProductBookingList || isProductBookingAdded
          ? BookingActions.setBookedTariffUuids({
              addBookedTariffUuids,
              removeBookedTariffUuids
            })
          : BookingActions.removeBookedTariffUuids(removeBookedTariffUuids);
      })
    ),
  { functional: true }
);

export const removeTicketHolderForms$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.removeProductFromBookingList,
        PackageProductBookingActions.removePackageFromBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        VoucherProductBookingActions.removeReleasedVoucherProductFromBookingList
      ),
      concatLatestFrom(() => [
        store.select(selectAllTicketHolderTariffUuidsFromBookedProducts),
        store.select(selectBookedTariffUuids)
      ]),
      filter(([_, __, previousBookedTariffUuids]) => !isEmpty(previousBookedTariffUuids)),
      map(([_, currentBookedTariffUuids, previousBookedTariffUuids]) =>
        BookingActions.removeBookedTariffUuids(difference(previousBookedTariffUuids, currentBookedTariffUuids))
      )
    ),
  { functional: true }
);
